import {Injectable} from '@angular/core';
import {TranslateService} from '@brunata-angular/util';
import {lastValueFrom, map, mergeMap, Observable, ReplaySubject} from 'rxjs';
import {AttachmentFile} from 'src/app/reporting/costs/models/file-attachment.model';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {sumBy} from 'lodash-es';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {environment} from 'src/environments/environment';

type ConvertedFile = {
    filename: string;
    attachment: string;
    mimeType: string;
};

export function formatBytes(bytes: number): string {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

@Injectable({
    providedIn: 'root',
})
export class AttachmentsUtil {
    constructor(
        private confirmationDialogService: ConfirmationDialogService,
        private translateService: TranslateService,
        private http: HttpClient,
    ) {}

    checkUploadSizeLimit(
        files: FileList,
        attachmentList: AttachmentFile[],
    ): {singleFileSizeLimitExceeded: boolean; totalFileSizeLimitExceeded: boolean} {
        const kbToMbFactor = 1024 ** 2;
        const uploadFilesSizeInMb = sumBy(Array.from(files), it => it.size) / kbToMbFactor;
        const attachmentsFilesSizeInMb =
            sumBy(attachmentList, it => it.attachmentSize) / kbToMbFactor;
        return {
            singleFileSizeLimitExceeded: Array.from(files).some(it => it.size / kbToMbFactor >= 20),
            totalFileSizeLimitExceeded: uploadFilesSizeInMb + attachmentsFilesSizeInMb >= 100,
        };
    }

    prepareAttachmentForUpload<T>(
        files: FileList,
        uploadFile: (convertedFile: ConvertedFile) => Observable<T>,
    ): Observable<T>[] {
        return Array.from(files).map(file =>
            this.convertFile(file).pipe(
                map(base64 => ({
                    filename: file.name,
                    mimeType:
                        file.type !== ''
                            ? file.type
                            : file.name.slice(-3) === 'msg'
                              ? 'application/vnd.ms-outlook'
                              : 'application/octet-stream',
                    attachment: base64,
                })),
                mergeMap(uploadFile),
            ),
        );
    }

    convertFile(file: File): Observable<string> {
        const result = new ReplaySubject<string>(1);
        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = (event: ProgressEvent<FileReader>): void =>
            result.next(btoa(event.target?.result?.toString() ?? ''));
        return result;
    }
    async downloadAttachment(file: AttachmentFile): Promise<void> {
        const response = await lastValueFrom(
            this.http.get(environment.baseUrl + file.attachmentUrl, {
                responseType: 'blob',
                observe: 'response',
            }),
        );
        if (response.body != null) {
            const objectUrl = URL.createObjectURL(response.body);
            const htmlAnchorElement = document.createElement('a');
            htmlAnchorElement.href = objectUrl;
            htmlAnchorElement.download = file.filename;
            htmlAnchorElement.click();
            URL.revokeObjectURL(objectUrl); // Clean up the object URL
        }
    }
    deleteAttachment(attachment: AttachmentFile, deleteHandler: () => void): void {
        void this.confirmationDialogService
            .confirm(
                this.translateService.translate('reporting_DELETE_ATTACHMENT_HEADER'),
                this.translateService
                    .translate('reporting_DELETE_ATTACHMENT_MSG')
                    .replace('{0}', attachment.filename),
                this.translateService.translate('reporting_CONFIRM'),
                this.translateService.translate('reporting_CANCEL'),
            )
            .then(response => {
                if (response) {
                    deleteHandler();
                }
            });
    }

    downloadBlob(response: HttpResponse<Blob>, fileName: string, fileType: string): void {
        const fileEnding = fileType === 'excel' ? 'xlsx' : 'csv';
        if (response.body != null) {
            const fileBlob = new Blob([response.body], {
                type: fileType,
            });
            const file = `${fileName}.${fileEnding}`;
            const downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(fileBlob);
            downloadLink.setAttribute('download', file);
            downloadLink.click();
        }
    }
}
